import type { KeyboardEvent, WheelEvent } from "react";
import { memo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { GlobalSearchTabButton } from "./GlobalSearchTabButton";
import type { IGlobalSearchTabsProps } from "./types";

const ACTIVE_TAB_SELECTOR = '[role="tab"][aria-selected="true"]';
const PREVIOUS_KEY = "ArrowLeft";
const NEXT_KEY = "ArrowRight";
const FIRST_KEY = "Home";
const LAST_KEY = "End";

// NOTE: NSAI-6853 — the category strip under the search box. It scrolls
// sideways: swiped on touch, dragged on a trackpad, and a plain mouse wheel
// is turned sideways too, since that is the only wheel most mice have. The
// edges fade so a cut-off chip reads as "more this way", and the active chip
// is always scrolled into view, including when ←/→ picked it.
const GlobalSearchTabsComponent = ({
  tabs,
  activeTab,
  onSelectTab,
  onSelectAdjacentTab,
}: IGlobalSearchTabsProps): JSX.Element => {
  const { t } = useTranslation("globalSearch");
  const listRef = useRef<HTMLDivElement>(null);

  // NOTE: Also on resize — a chip in view on a wide window can fall out of it
  // when the window narrows.
  useEffect(() => {
    const revealActiveTab = (): void => {
      listRef.current
        ?.querySelector(ACTIVE_TAB_SELECTOR)
        ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    };
    revealActiveTab();
    globalThis.addEventListener("resize", revealActiveTab);
    return () => {
      globalThis.removeEventListener("resize", revealActiveTab);
    };
  }, [activeTab]);

  // NOTE: NSAI-6853 — once a chip has keyboard focus the strip is a regular
  // tablist: ←/→ (in reading order) and Home/End move the selection, and the
  // focus follows it, since only the selected chip is in the tab order.
  useEffect(() => {
    const list = listRef.current;
    if (!list?.contains(document.activeElement)) return;
    list.querySelector<HTMLElement>(ACTIVE_TAB_SELECTOR)?.focus();
  }, [activeTab]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      const isRtl = getComputedStyle(event.currentTarget).direction === "rtl";
      if (event.key === PREVIOUS_KEY || event.key === NEXT_KEY) {
        onSelectAdjacentTab((event.key === NEXT_KEY) !== isRtl ? 1 : -1);
      } else if (event.key === FIRST_KEY || event.key === LAST_KEY) {
        const edge = event.key === FIRST_KEY ? tabs[0] : tabs[tabs.length - 1];
        if (edge) onSelectTab(edge.tab);
      } else {
        return;
      }
      // NOTE: Handled here, so cmdk does not also move the result highlight.
      event.preventDefault();
      event.stopPropagation();
    },
    [onSelectAdjacentTab, onSelectTab, tabs],
  );

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>): void => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.currentTarget.scrollLeft += event.deltaY;
  }, []);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={t("tab_list_label")}
      aria-orientation="horizontal"
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      className="scrollbar-none flex gap-1 overflow-x-auto scroll-smooth px-3 py-2 [mask-image:linear-gradient(to_right,transparent,black_12px,black_calc(100%-12px),transparent)] motion-reduce:scroll-auto sm:px-4"
      data-testid="global-search-tabs"
    >
      {tabs.map((definition) => (
        <GlobalSearchTabButton
          key={definition.tab}
          definition={definition}
          isActive={definition.tab === activeTab}
          onSelectTab={onSelectTab}
        />
      ))}
    </div>
  );
};

export const GlobalSearchTabs = memo(GlobalSearchTabsComponent);
